import { useEffect, useMemo } from "react";

import { KpkApiOrientationContactSheet, KpkApiUser } from "@keepeek/api-client";
import {
  fetcherModeAtom,
  DataViewKey,
  KpkMedia,
  currentUserQuery,
  SentryBreadcrumbCategory,
  selectedElements,
} from "@keepeek/commons";
import { KpkContactSheet } from "@keepeek/refront-components";
import * as Sentry from "@sentry/nextjs";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  SetterOrUpdater,
  useRecoilState,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from "recoil";

import { getAxiosClientInstance } from "../../../lib/axios/axios-utils";
import logger from "../../../lib/logger-utils";
import { downloadManagerFetcherSelector } from "../../fetcher/selectors/downloadManager";
import {
  contactSheetsModalOpenState,
  contactSheetsModalSelectedElementsState,
} from "../atoms/selections";
import { generateContactSheet } from "../utils";

import { useContactSheets } from "./index";

type useContactSheetSelectionsProps = {
  open: boolean;
  close: () => void;
  count: number;
  loading: boolean;
  contactSheetList: KpkContactSheet[] | null;
  onSelectContactSheet: (id, orientation) => void;
  setSelectionToExport: SetterOrUpdater<KpkMedia["id"][]>;
};

function useContactSheetSelections(): useContactSheetSelectionsProps {
  const [open, setOpen] = useRecoilState(contactSheetsModalOpenState);
  const [selectionToExport, setSelectionToExport] = useRecoilState(
    contactSheetsModalSelectedElementsState,
  );
  const { contactSheets, loadingContactSheets } = useContactSheets();
  const setSelectedElements = useSetRecoilState(selectedElements(DataViewKey.SEARCH));

  const snackBar = useSnackbar();
  const userQuery = useRecoilValueLoadable(currentUserQuery);
  const user: KpkApiUser = useMemo(
    () => (userQuery.state === "hasValue" ? userQuery.contents : {}),
    [userQuery.contents, userQuery.state],
  );
  const { t } = useTranslation();
  const mode = useRecoilValue(fetcherModeAtom);
  const downloadManagerFetcherSelectorQuery = useRecoilValueLoadable(
    downloadManagerFetcherSelector(mode),
  );

  async function onSelectContactSheet(
    contactSheetId: KpkContactSheet["id"],
    orientation: KpkApiOrientationContactSheet,
  ) {
    if (downloadManagerFetcherSelectorQuery.state === "hasValue") {
      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.USER_INTERACTION,
        message: `onSelectContactSheet: ${contactSheetId}, orientation: ${orientation}`,
      });
      const { postDownloadRequests, getDownloadRequests } =
        downloadManagerFetcherSelectorQuery.contents;
      const res = await generateContactSheet(
        getAxiosClientInstance(),
        postDownloadRequests,
        getDownloadRequests,
        contactSheetId,
        snackBar,
        t,
        null,
        orientation,
        user,
        selectionToExport,
      );
      logger.debug(res);
      setOpen(false);
    }
  }

  function close() {
    setOpen(false);
    // Remove the selected elements from export to ContactSheets
    setSelectionToExport([]);
    // Clear the dataView from selection
    setSelectedElements([]);

    Sentry.addBreadcrumb({
      category: SentryBreadcrumbCategory.VISUAL_FEEDBACK,
      message: `contactSheetsModalOpenState: close`,
    });
  }
  useEffect(() => {
    if (open) {
      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.VISUAL_FEEDBACK,
        message: `contactSheetsModalOpenState: open`,
      });
    }
  }, [open]);

  return {
    open,
    close,
    count: selectionToExport.length,
    loading: loadingContactSheets,
    contactSheetList: contactSheets || null,
    onSelectContactSheet,
    setSelectionToExport,
  };
}

export default useContactSheetSelections;
